import type { SortingState } from '@tanstack/vue-table';

import { nextTick, watch, type Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { queryToRecord } from '@/shared/lib';

interface PlatformRegistryUrlStateOptions {
  search: Ref<string>;
  organizationId?: Ref<string | null>;
  sorting?: Ref<SortingState>;
  defaultSorting?: SortingState;
  sortableIds?: readonly string[];
}

const sortingEqual = (left: SortingState, right: SortingState): boolean =>
  left.length === right.length &&
  left.every((item, index) => item.id === right[index]?.id && item.desc === right[index].desc);

export const usePlatformRegistryUrlState = (options: PlatformRegistryUrlStateOptions): void => {
  const route = useRoute();
  const router = useRouter();
  const defaultSorting = options.defaultSorting ?? [];
  let applyingFromUrl = false;

  const applyFromUrl = (): void => {
    const query = queryToRecord(route.query);

    applyingFromUrl = true;
    options.search.value = query.q ?? '';

    if (options.organizationId) {
      options.organizationId.value = query.organization || null;
    }

    if (options.sorting) {
      const sort = query.sort;
      const isAllowedSort = Boolean(sort && options.sortableIds?.includes(sort));
      options.sorting.value = isAllowedSort
        ? [{ id: sort as string, desc: query.order === 'desc' }]
        : [...defaultSorting];
    }

    void nextTick(() => {
      applyingFromUrl = false;
    });
  };

  watch(() => route.query, applyFromUrl, { immediate: true });

  watch(
    [
      options.search,
      ...(options.organizationId ? [options.organizationId] : []),
      ...(options.sorting ? [options.sorting] : []),
    ],
    () => {
      if (applyingFromUrl) {
        return;
      }

      const query = { ...route.query };

      if (options.search.value.trim()) {
        query.q = options.search.value.trim();
      } else {
        delete query.q;
      }

      delete query.limit;

      if (options.organizationId?.value) {
        query.organization = options.organizationId.value;
      } else {
        delete query.organization;
      }

      const currentSorting = options.sorting?.value ?? [];
      const head = currentSorting[0];

      if (head && !sortingEqual(currentSorting, defaultSorting)) {
        query.sort = head.id;
        query.order = head.desc ? 'desc' : 'asc';
      } else {
        delete query.sort;
        delete query.order;
      }

      void router.replace({ query });
    },
    { deep: true },
  );
};
